import React, { useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, CirclePlay, Cpu, Github, Play, Sparkles } from 'lucide-react';
import { cx, UI } from '../gitShared';
import { cogShowcases, type CogShowcase, type CogShowcaseKind } from './cogShowcaseData';

const KIND_LABEL: Record<CogShowcaseKind, string> = {
  motion: 'Motion',
  video: 'Video',
  voxel: '3D',
  clean: 'Image',
  depth: 'VFX',
  vector: 'Vector',
  upscale: 'Image',
  stems: 'Audio',
  cutout: 'Image',
  speech: 'Speech',
  text: 'LLM',
  image: 'Image',
  music: 'Music',
};

const thumbFor = (cog: CogShowcase) => {
  if (cog.outputMedia && cog.kind !== 'music') return cog.outputMedia;
  if (cog.inputImage) return cog.inputImage;
  return '';
};

const promptPreview = (cog: CogShowcase) =>
  String(cog.input.prompt || cog.input.text || cog.input.input || cog.input.style || '').slice(0, 120);

export const CogExamplesHub: React.FC = () => {
  const [kind, setKind] = useState<'all' | CogShowcaseKind>('all');
  const [query, setQuery] = useState('');
  const kinds = useMemo(() => ['all', ...Array.from(new Set(cogShowcases.map((c) => c.kind)))] as const, []);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cogShowcases.filter((cog) => {
      if (kind !== 'all' && cog.kind !== kind) return false;
      if (!q) return true;
      const hay = [cog.title, cog.template, cog.eyebrow, cog.description, ...cog.tags].join(' ').toLowerCase();
      return q.split(/\s+/).every((token) => hay.includes(token));
    });
  }, [kind, query]);

  return (
    <main className="overflow-hidden text-white">
      <section className="relative border-b border-white/8 px-5 pb-12 pt-10 sm:px-8 lg:pb-16 lg:pt-14">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(34,211,238,.12),transparent_30%),radial-gradient(circle_at_88%_8%,rgba(139,92,246,.12),transparent_28%)]" />
        <div className="relative mx-auto max-w-7xl">
          <a href="/cogs" className="inline-flex items-center gap-2 text-xs font-semibold text-white/45 transition hover:text-white"><ChevronLeft size={15} /> Cog Studio</a>
          <p className="mt-8 text-sm font-bold uppercase tracking-[0.2em] text-cyan-300/80">Open Cogs</p>
          <h1 className="mt-2 max-w-4xl text-balance text-4xl font-black tracking-[-0.045em] sm:text-6xl">Example recipes with verified defaults</h1>
          <p className="mt-5 max-w-3xl text-pretty text-base leading-7 text-white/58 sm:text-lg">
            Each card links to a canonical input/output showcase. Deploy the same template name with the bundled JSON — HTTP, CLI, desktop, and MCP stay in parity.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {kinds.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setKind(value)}
                className={cx(
                  'rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors',
                  kind === value ? 'border-white bg-white text-black' : 'border-white/12 text-white/52 hover:border-white/30 hover:text-white',
                )}
              >
                {value === 'all' ? 'All' : KIND_LABEL[value]}
              </button>
            ))}
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search examples…"
            aria-label="Search Cog examples"
            className={cx(UI.input, 'h-9 w-full text-xs lg:w-72')}
          />
        </div>

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((cog) => {
            const thumb = thumbFor(cog);
            const isVideo = cog.kind === 'video' && !!cog.outputMedia;
            return (
              <a
                key={cog.slug}
                href={`/cogs/${cog.slug}`}
                className="group flex min-h-[320px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition hover:border-white/22 hover:bg-white/[0.045]"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-black/40">
                  {thumb ? (
                    isVideo ? (
                      <video src={thumb} muted loop autoPlay playsInline className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]" />
                    ) : (
                      <img src={thumb} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]" />
                    )
                  ) : (
                    <div className="flex h-full items-center justify-center p-6 text-center">
                      <p className="text-sm font-semibold leading-6 text-white/70">{promptPreview(cog)}</p>
                    </div>
                  )}
                  {isVideo && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white/75">
                      <CirclePlay size={11} /> video
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-300/75">{cog.eyebrow}</p>
                  <h2 className="mt-1 text-lg font-black tracking-tight">{cog.title}</h2>
                  <p className="mt-2 line-clamp-2 text-xs leading-5 text-white/45">{cog.description}</p>
                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-4 text-[10px] text-white/40">
                    <span className="inline-flex items-center gap-1"><Cpu size={11} /> {cog.hardware}</span>
                    <span className="font-mono">template:{cog.template}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={cx(UI.btn, UI.btnPrimary, 'h-8 px-3 text-[11px]')}><Play size={12} /> View example</span>
                    {cog.source && (
                      <span
                        role="link"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(cog.source, '_blank', 'noreferrer'); }}
                        className={cx(UI.btn, UI.btnGhost, 'h-8 px-2.5 text-[11px]')}
                      >
                        <Github size={12} />
                      </span>
                    )}
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {visible.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/45">
            <Sparkles className="mx-auto mb-3 text-white/25" size={32} />
            No examples match that filter.
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-cyan-300/15 bg-cyan-300/[0.04] p-6">
          <div>
            <p className="text-sm font-bold text-white/85">Ready to ship your own?</p>
            <p className="mt-1 text-xs text-white/45">Pick a template in Cog Studio, or register any compliant image once and reuse it everywhere.</p>
          </div>
          <a href="/cogs" className={cx(UI.btn, UI.btnOutline, 'h-10 px-4')}>Open Cog Studio <ArrowRight size={14} /></a>
        </div>
      </section>
    </main>
  );
};
